import React, { useEffect, useState } from 'react';
import { Building2, Plus, Loader2, MapPin, Users, IndianRupee, ArrowRightLeft, Shield, X, ShieldCheck, Percent, Save, Settings2, KeyRound, Trash2, UserPlus, ClipboardCopy, Lock } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { useApp } from '../../context/AppContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const AdminFranchises = () => {
  const { hasPermission } = useApp();
  const canEdit = hasPermission('admin_franchises', 'edit');
  const canDelete = hasPermission('admin_franchises', 'delete');
  const [data, setData] = useState(null);
  const [franchiseCfg, setFranchiseCfg] = useState({}); // fid -> {commission_pct, gst_pct}
  const [loading, setLoading] = useState(true);
  const [districts, setDistricts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [editingFid, setEditingFid] = useState(null);
  const [commissionForm, setCommissionForm] = useState({ company: 10, franchise: 20, cleaner: 70, gst_pct: 18 });
  const [createForm, setCreateForm] = useState({ district: '', admin_email: '', currency: 'INR' });
  const [xferForm, setXferForm] = useState({ user_id: '', target_franchise_id: '' });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  // Manage franchise drawer (iter-15c)
  const [manageFid, setManageFid] = useState(null);
  const [manageData, setManageData] = useState(null);
  const [manageTab, setManageTab] = useState('details');
  const [detailsForm, setDetailsForm] = useState({});
  const [userForm, setUserForm] = useState({ name: '', email: '', phone: '', role: 'staff', staff_role: 'manager', password: '' });
  // Create Chief Accountant (iter-17)
  const [showCreateCa, setShowCreateCa] = useState(false);
  const [caForm, setCaForm] = useState({ name: '', email: '', phone: '', password: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [d, dist, wk, fr] = await Promise.all([
        axios.get(`${API}/admin/global-dashboard`, authCfg()),
        axios.get(`${API}/franchises/districts`),
        axios.get(`${API}/admin/workers`, authCfg()),
        axios.get(`${API}/franchises`, authCfg()),
      ]);
      setData(d.data);
      setDistricts(dist.data.districts || []);
      setWorkers(wk.data.workers || []);
      const cfg = {};
      (fr.data || []).forEach((f) => { cfg[f.franchise_id] = { commission_pct: f.commission_pct, gst_pct: f.gst_pct }; });
      setFranchiseCfg(cfg);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!createForm.district) { setMsg({ ok: false, text: 'District required' }); return; }
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/franchises`, createForm, authCfg());
      setMsg({ ok: true, text: `Franchise ${r.franchise.franchise_id} created. Assign an admin user manually via "Manage → Users".` });
      setShowCreate(false);
      setCreateForm({ district: '', admin_email: '', currency: 'INR' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to create' });
    } finally { setBusy(false); }
  };

  const transfer = async () => {
    if (!xferForm.user_id || !xferForm.target_franchise_id) { setMsg({ ok: false, text: 'Pick a worker and a target franchise' }); return; }
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/franchises/staff-transfer`, xferForm, authCfg());
      setMsg({ ok: true, text: `Worker moved to ${r.district} (${r.to})` });
      setShowTransfer(false);
      setXferForm({ user_id: '', target_franchise_id: '' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Transfer failed' });
    } finally { setBusy(false); }
  };

  const openCommission = (fid) => {
    const cfg = franchiseCfg[fid] || {};
    const c = cfg.commission_pct || { company: 10, franchise: 20, cleaner: 70 };
    setCommissionForm({ company: c.company, franchise: c.franchise, cleaner: c.cleaner, gst_pct: cfg.gst_pct ?? 18 });
    setEditingFid(fid);
    setMsg(null);
  };

  const saveCommission = async () => {
    const { company, franchise, cleaner, gst_pct } = commissionForm;
    const sum = Number(company) + Number(franchise) + Number(cleaner);
    if (sum !== 100) { setMsg({ ok: false, text: `Commissions must sum to 100 (currently ${sum})` }); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/franchises/${editingFid}`, {
        commission_pct: { company: Number(company), franchise: Number(franchise), cleaner: Number(cleaner) },
        gst_pct: Number(gst_pct),
      }, authCfg());
      setMsg({ ok: true, text: `Commission updated for ${editingFid}` });
      setEditingFid(null);
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Update failed' });
    } finally { setBusy(false); }
  };

  // ---- Manage franchise drawer (iter-15c) ----
  const openManage = async (fid) => {
    setManageFid(fid);
    setManageTab('details');
    setManageData(null);
    setUserForm({ name: '', email: '', phone: '', role: 'staff', staff_role: 'manager', password: '' });
    setMsg(null);
    try {
      const { data: r } = await axios.get(`${API}/admin/franchises/${fid}/users`, authCfg());
      setManageData(r);
      const f = r.franchise || {};
      setDetailsForm({
        district: f.district || '',
        state: f.state || '',
        country: f.country || 'IN',
        admin_email: f.admin_email || '',
        address: f.address || '',
        notes: f.notes || '',
        active: f.active !== false,
      });
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to load' }); }
  };

  const reloadManage = async () => {
    if (!manageFid) return;
    try { const { data: r } = await axios.get(`${API}/admin/franchises/${manageFid}/users`, authCfg()); setManageData(r); } catch {}
  };

  const saveDetails = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/franchises/${manageFid}`, detailsForm, authCfg());
      setMsg({ ok: true, text: `${manageFid} updated` });
      await reloadManage();
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Update failed' }); }
    finally { setBusy(false); }
  };

  const createFranchiseUser = async () => {
    if (!userForm.name || !userForm.email) { setMsg({ ok: false, text: 'Name & email required' }); return; }
    setBusy(true);
    try {
      const payload = { ...userForm, franchise_id: manageFid };
      if (!payload.password) delete payload.password;
      if (payload.role !== 'staff') delete payload.staff_role;
      const { data: r } = await axios.post(`${API}/franchise/users`, payload, authCfg());
      setUserForm({ name: '', email: '', phone: '', role: 'staff', staff_role: 'manager', password: '' });
      setMsg({ ok: true, text: `Created ${payload.role}${payload.staff_role ? ' · ' + payload.staff_role : ''}: ${r.user?.email || payload.email}` });
      await reloadManage();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Create failed' }); }
    finally { setBusy(false); }
  };

  const resetUserPassword = async (u) => {
    const newPw = window.prompt(`Enter a NEW password for ${u.name} (${u.email}) — minimum 6 characters:`);
    if (!newPw) return;
    if (newPw.length < 6) { setMsg({ ok: false, text: 'Password must be at least 6 characters' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/${u.user_id}/reset-password`, { new_password: newPw }, authCfg());
      setMsg({ ok: true, text: `Password updated for ${u.email}` });
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Reset failed' }); }
    finally { setBusy(false); }
  };

  const deleteFranchiseUser = async (u) => {
    if (u.role === 'admin') return;
    if (!window.confirm(`Delete user ${u.name} (${u.email})?`)) return;
    try {
      await axios.delete(`${API}/franchise/users/${u.user_id}`, authCfg());
      setMsg({ ok: true, text: `Removed ${u.email}` });
      await reloadManage();
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Delete failed' }); }
  };

  const createChiefAccountant = async () => {
    if (!caForm.name || !caForm.email) { setMsg({ ok: false, text: 'Name & email required' }); return; }
    setBusy(true);
    try {
      const payload = { ...caForm };
      if (!payload.password) delete payload.password;
      const { data: r } = await axios.post(`${API}/admin/create-chief-accountant`, payload, authCfg());
      setMsg({ ok: true, text: `Chief Accountant created: ${r.user.email}` });
      setCaForm({ name: '', email: '', phone: '', password: '' });
      setShowCreateCa(false);
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Create failed' }); }
    finally { setBusy(false); }
  };

  return (
    <AdminLayout title="Franchise Network (Kerala Pilot)">
      {loading || !data ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
      ) : (
        <>
          {msg && (
            <div data-testid="admin-franchises-msg" className={`mb-4 text-sm rounded-lg px-3 py-2 ${msg.ok ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>
          )}
          {/* Credentials banner removed (security policy 2026-02) — passwords never displayed */}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Franchises"   value={data.summary.total_franchises}  icon={Building2}   color="#2E7D32" bg="#E8F5E9" testid="admin-fr-count" />
            <Stat label="Total revenue" value={`₹${data.summary.total_revenue.toLocaleString('en-IN')}`} icon={IndianRupee} color="#EA580C" bg="#FFF3E0" testid="admin-fr-revenue" />
            <Stat label="Total customers" value={data.summary.total_customers} icon={Users} color="#1E88E5" bg="#DBEAFE" testid="admin-fr-customers" />
            <Stat label="Total workers"   value={data.summary.total_workers}   icon={Shield} color="#7B1FA2" bg="#F3E5F5" testid="admin-fr-workers" />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button data-testid="admin-fr-create-btn" onClick={() => { setShowCreate(true); setMsg(null); }} className="inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors">
              <Plus className="w-4 h-4" /> Create franchise
            </button>
            <button data-testid="admin-fr-transfer-btn" onClick={() => { setShowTransfer(true); setMsg(null); }} className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 hover:border-[#2E7D32] font-semibold text-sm px-4 py-2 rounded-lg text-neutral-700 transition-colors">
              <ArrowRightLeft className="w-4 h-4" /> Staff transfer
            </button>
            <button data-testid="admin-fr-create-ca-btn" onClick={() => { setShowCreateCa(true); setMsg(null); setCaCredentials(null); }} className="inline-flex items-center gap-1.5 bg-[#4338CA] hover:bg-[#3730A3] text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors ml-auto">
              <ShieldCheck className="w-4 h-4" /> Create Chief Accountant
            </button>
          </div>

          <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden" data-testid="admin-fr-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                  <tr><Th>Franchise</Th><Th>Admin</Th><Th>Commission %</Th><Th>GST</Th><Th>Customers</Th><Th>Workers</Th><Th className="text-right">Revenue</Th><Th className="text-right">Action</Th></tr>
                </thead>
                <tbody>
                  {data.franchises.map((f) => {
                    const cfg = franchiseCfg[f.franchise_id] || {};
                    const c = cfg.commission_pct || { company: '?', franchise: '?', cleaner: '?' };
                    return (
                    <tr key={f.franchise_id} data-testid={`admin-fr-row-${f.franchise_id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-4 py-3">
                        <p className="font-semibold flex items-center gap-1"><MapPin className="w-3 h-3 text-[#2E7D32]" /> {f.district}</p>
                        <p className="text-xs text-neutral-500 font-mono">{f.franchise_id}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-600">{f.admin_email}</td>
                      <td className="px-4 py-3 text-xs" data-testid={`fr-commission-${f.franchise_id}`}>
                        <span className="font-mono">
                          <span className="text-[#7B1FA2]">C {c.company}</span> · <span className="text-[#2E7D32]">F {c.franchise}</span> · <span className="text-[#D97706]">W {c.cleaner}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-bold">{cfg.gst_pct ?? '—'}%</td>
                      <td className="px-4 py-3 text-xs">{f.customers}</td>
                      <td className="px-4 py-3 text-xs">{f.workers}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#2E7D32]">₹{(f.revenue || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`fr-manage-${f.franchise_id}`}
                          onClick={() => openManage(f.franchise_id)}
                          className="inline-flex items-center gap-1 bg-[#7B1FA2] hover:bg-[#6A1B9A] text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg mr-1">
                          <Settings2 className="w-3.5 h-3.5" /> Manage
                        </button>
                        <button
                          data-testid={`fr-edit-commission-${f.franchise_id}`}
                          onClick={() => openCommission(f.franchise_id)}
                          className="inline-flex items-center gap-1 bg-white border border-neutral-200 hover:border-[#2E7D32] hover:text-[#2E7D32] text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors mr-1">
                          <Percent className="w-3.5 h-3.5" /> Commission
                        </button>
                        {f.status === 'suspended' ? (
                          <button
                            data-testid={`fr-unsuspend-${f.franchise_id}`}
                            onClick={async () => { try { await axios.post(`${API}/admin/franchises/${f.franchise_id}/unsuspend`, {}, authCfg()); load(); } catch(e){setMsg({ok:false,text:e?.response?.data?.detail||'Failed'});} }}
                            className="inline-flex items-center gap-1 bg-[#2E7D32] hover:bg-[#276D2A] text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg">
                            Unsuspend
                          </button>
                        ) : (
                          <button
                            data-testid={`fr-suspend-${f.franchise_id}`}
                            onClick={async () => {
                              const reason = window.prompt(`Suspend ${f.district}? Enter reason:`, '');
                              if (reason === null) return;
                              try { await axios.post(`${API}/admin/franchises/${f.franchise_id}/suspend`, { reason }, authCfg()); load(); setMsg({ok:true,text:`Suspended ${f.district}`}); }
                              catch(e){setMsg({ok:false,text:e?.response?.data?.detail||'Failed'});}
                            }}
                            className="inline-flex items-center gap-1 bg-white border border-red-200 hover:bg-red-50 text-red-600 text-xs font-semibold px-2.5 py-1.5 rounded-lg">
                            Suspend
                          </button>
                        )}
                      </td>
                    </tr>
                  );})}
                  {data.franchises.length === 0 && <tr><td colSpan="8" className="text-center text-neutral-500 py-8">No franchises yet — create one to get started.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-neutral-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2E7D32]" /> C = Company, F = Franchise, W = Worker (Cleaner). Splits apply on every paid booking after GST.</p>
        </>
      )}

      {/* Create Franchise modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Create franchise branch">
          <label className="block text-xs font-semibold text-neutral-600">District</label>
          <select data-testid="admin-fr-form-district" value={createForm.district} onChange={(e) => setCreateForm({ ...createForm, district: e.target.value })}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="">Select district</option>
            {districts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Franchise admin email</label>
          <input data-testid="admin-fr-form-email" type="email" value={createForm.admin_email} onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })}
            placeholder="admin@franchise-thrissur.com"
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Currency</label>
          <select data-testid="admin-fr-form-currency" value={createForm.currency} onChange={(e) => setCreateForm({ ...createForm, currency: e.target.value })}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            {['INR', 'USD', 'AED', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button data-testid="admin-fr-form-submit" onClick={create} disabled={busy} className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Creating…' : 'Create franchise'}
          </button>
          <p className="mt-2 text-[10px] text-neutral-500">Admin email is optional. If provided and the user already exists, they'll be assigned as franchise admin. New admin users must be created manually via "Manage → Add User".</p>
        </Modal>
      )}

      {/* Staff transfer modal */}
      {showTransfer && (
        <Modal onClose={() => setShowTransfer(false)} title="Transfer staff between franchises">
          <label className="block text-xs font-semibold text-neutral-600">Worker</label>
          <select data-testid="admin-xfer-worker" value={xferForm.user_id} onChange={(e) => setXferForm({ ...xferForm, user_id: e.target.value })}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="">Select worker</option>
            {workers.map((w) => <option key={w.user_id} value={w.user_id}>{w.name || w.email} — {w.district || 'Unassigned'}</option>)}
          </select>
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Move to franchise</label>
          <select data-testid="admin-xfer-target" value={xferForm.target_franchise_id} onChange={(e) => setXferForm({ ...xferForm, target_franchise_id: e.target.value })}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="">Select target franchise</option>
            {data.franchises.map((f) => <option key={f.franchise_id} value={f.franchise_id}>{f.district} ({f.franchise_id})</option>)}
          </select>
          <button data-testid="admin-xfer-submit" onClick={transfer} disabled={busy} className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Transferring…' : 'Transfer worker'}
          </button>
        </Modal>
      )}

      {/* Commission editor modal */}
      {editingFid && (
        <Modal onClose={() => setEditingFid(null)} title={`Commission split — ${editingFid}`}>          <p className="text-xs text-neutral-500 mb-3">These percentages are applied to every paid booking after GST is separated. Must sum to 100.</p>
          <div className="grid grid-cols-3 gap-2">
            <NumField label="Company %" testid="commission-company" value={commissionForm.company} onChange={(v) => setCommissionForm({ ...commissionForm, company: v })} color="#7B1FA2" />
            <NumField label="Franchise %" testid="commission-franchise" value={commissionForm.franchise} onChange={(v) => setCommissionForm({ ...commissionForm, franchise: v })} color="#2E7D32" />
            <NumField label="Worker %" testid="commission-cleaner" value={commissionForm.cleaner} onChange={(v) => setCommissionForm({ ...commissionForm, cleaner: v })} color="#D97706" />
          </div>
          <div className="mt-2 text-xs text-neutral-500">Sum: <span className={`font-bold ${(Number(commissionForm.company)+Number(commissionForm.franchise)+Number(commissionForm.cleaner))===100 ? 'text-[#2E7D32]' : 'text-red-600'}`}>{Number(commissionForm.company)+Number(commissionForm.franchise)+Number(commissionForm.cleaner)}%</span></div>
          <div className="mt-4">
            <NumField label="GST %" testid="commission-gst" value={commissionForm.gst_pct} onChange={(v) => setCommissionForm({ ...commissionForm, gst_pct: v })} color="#1E88E5" />
          </div>
          <button data-testid="commission-save-btn" onClick={saveCommission} disabled={busy} className="mt-4 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            <Save className="w-4 h-4" /> {busy ? 'Saving…' : 'Save commission'}
          </button>
        </Modal>
      )}

      {/* Manage franchise drawer (iter-15c) */}
      {manageFid && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setManageFid(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="fr-manage-drawer" className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-neutral-100 flex items-center gap-2 bg-[#7B1FA2] text-white">
              <Building2 className="w-5 h-5" />
              <div className="flex-1">
                <p className="font-extrabold text-sm">Manage franchise · {manageData?.franchise?.district || manageFid}</p>
                <p className="text-[11px] text-purple-100 font-mono">{manageFid}</p>
              </div>
              <button onClick={() => { setManageFid(null); }} className="text-white hover:text-purple-100"><X className="w-5 h-5" /></button>
            </div>

            <div className="px-5 pt-3 flex gap-2 border-b border-neutral-100 bg-neutral-50">
              {[['details', 'Details', Settings2], ['users', 'Users & Staff', Users], ['add', 'Add User', UserPlus]].map(([k, label, Icon]) => (
                <button key={k} data-testid={`fr-manage-tab-${k}`} onClick={() => setManageTab(k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-lg -mb-px ${manageTab === k ? 'bg-white border border-neutral-200 border-b-white text-[#7B1FA2]' : 'text-neutral-600 hover:text-[#7B1FA2]'}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {!manageData && <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-neutral-400" /></div>}

              {/* Credentials banner removed (security policy 2026-02) */}

              {manageData && manageTab === 'details' && (
                <div data-testid="fr-manage-details" className="space-y-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <Fld label="District"    testid="det-district"    value={detailsForm.district}    onChange={(v) => setDetailsForm({ ...detailsForm, district: v })} />
                    <Fld label="State"       testid="det-state"       value={detailsForm.state}       onChange={(v) => setDetailsForm({ ...detailsForm, state: v })} />
                    <Fld label="Country"     testid="det-country"     value={detailsForm.country}     onChange={(v) => setDetailsForm({ ...detailsForm, country: v })} />
                    <Fld label="Admin email" testid="det-admin"       value={detailsForm.admin_email} onChange={(v) => setDetailsForm({ ...detailsForm, admin_email: v })} />
                    <Fld label="Address"     testid="det-address"     value={detailsForm.address}     onChange={(v) => setDetailsForm({ ...detailsForm, address: v })} full />
                    <Fld label="Notes"       testid="det-notes"       value={detailsForm.notes}       onChange={(v) => setDetailsForm({ ...detailsForm, notes: v })} full />
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                    <input data-testid="det-active" type="checkbox" checked={detailsForm.active !== false} onChange={(e) => setDetailsForm({ ...detailsForm, active: e.target.checked })} />
                    <span className="font-semibold">Active</span>
                    <span className="text-neutral-500">(uncheck to freeze new bookings without full suspend)</span>
                  </label>
                  <div className="pt-3 border-t border-neutral-100">
                    <button data-testid="fr-manage-save-details" onClick={saveDetails} disabled={busy || !canEdit}
                      className="inline-flex items-center gap-1.5 py-2 px-4 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm"
                      title={canEdit ? '' : 'You lack Edit permission for Franchises'}>
                      {canEdit ? <Save className="w-4 h-4" /> : <Lock className="w-4 h-4" />} {busy ? 'Saving…' : 'Save details'}
                    </button>
                  </div>
                </div>
              )}

              {manageData && manageTab === 'users' && (
                <div data-testid="fr-manage-users">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-neutral-500 uppercase">Users in this franchise ({(manageData.users || []).length})</p>
                    <button data-testid="fr-manage-goto-add" onClick={() => setManageTab('add')} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#7B1FA2] hover:underline">
                      <UserPlus className="w-3.5 h-3.5" /> Add new user
                    </button>
                  </div>
                  <div className="border border-neutral-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                        <tr>
                          <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Joined</Th><Th className="text-right">Actions</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {(manageData.users || []).map((u) => (
                          <tr key={u.user_id} data-testid={`fr-manage-user-${u.user_id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                            <td className="px-4 py-2.5">
                              <p className="text-xs font-semibold">{u.name}</p>
                              <p className="text-[10px] text-neutral-400 font-mono">{u.user_id}</p>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-neutral-600">{u.email}</td>
                            <td className="px-4 py-2.5">
                              <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${u.role === 'cleaner' ? 'bg-[#FFF3E0] text-[#EA580C]' : u.role === 'staff' ? 'bg-[#DBEAFE] text-[#1E88E5]' : u.role === 'franchise_admin' ? 'bg-[#F3E5F5] text-[#7B1FA2]' : u.role === 'admin' ? 'bg-neutral-900 text-white' : 'bg-[#E8F5E9] text-[#2E7D32]'}`}>
                                {u.role}{u.staff_role ? ` · ${u.staff_role}` : ''}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-[11px] text-neutral-500">{(u.created_at || '').slice(0, 10)}</td>
                            <td className="px-4 py-2.5 text-right space-x-1">
                              <button data-testid={`fr-manage-reset-${u.user_id}`} onClick={() => resetUserPassword(u)} disabled={busy || !canEdit}
                                className="inline-flex items-center gap-1 bg-white border border-neutral-200 hover:border-[#7B1FA2] hover:text-[#7B1FA2] text-[11px] font-semibold px-2 py-1 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                                title={canEdit ? 'Reset password' : 'You lack Edit permission for Franchises'}>
                                {canEdit ? <KeyRound className="w-3 h-3" /> : <Lock className="w-3 h-3" />} Reset PW
                              </button>
                              {u.role !== 'admin' && canDelete && (
                                <button data-testid={`fr-manage-delete-${u.user_id}`} onClick={() => deleteFranchiseUser(u)}
                                  className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 text-[11px] font-semibold px-2 py-1">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                              {u.role !== 'admin' && !canDelete && (
                                <span data-testid={`fr-manage-delete-locked-${u.user_id}`} className="inline-flex items-center gap-1 text-neutral-300 text-[11px] px-2 py-1" title="You lack Delete permission for Franchises">
                                  <Lock className="w-3 h-3" />
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {(manageData.users || []).length === 0 && <tr><td colSpan={5} className="text-center py-8 text-xs text-neutral-400">No users yet in this franchise.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {manageData && manageTab === 'add' && (
                <div data-testid="fr-manage-add" className="max-w-lg">
                  <p className="text-xs text-neutral-500 mb-3">Create any user under franchise <span className="font-bold">{manageData.franchise?.district}</span>. You must set the initial password below (never auto-generated or displayed).</p>

                  <label className="block text-xs font-semibold text-neutral-600">Role</label>
                  <select data-testid="add-user-role" value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
                    <option value="customer">Customer</option>
                    <option value="cleaner">Cleaner / Worker</option>
                    <option value="staff">Staff (Manager / Supervisor / Accountant …)</option>
                    <option value="franchise_admin">Franchise Admin (owner)</option>
                  </select>

                  {userForm.role === 'staff' && (
                    <>
                      <label className="mt-3 block text-xs font-semibold text-neutral-600">Staff position</label>
                      <select data-testid="add-user-staff-role" value={userForm.staff_role} onChange={(e) => setUserForm({ ...userForm, staff_role: e.target.value })}
                        className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
                        <option value="manager">Manager</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="field_supervisor">Field Supervisor</option>
                        <option value="operations_manager">Operations Manager</option>
                        <option value="accountant">Accountant</option>
                        <option value="chief_accountant">Chief Accountant</option>
                        <option value="generic">Generic Staff</option>
                      </select>
                    </>
                  )}

                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600">Full name</label>
                      <input data-testid="add-user-name" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                        className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="Ravi Kumar" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600">Email</label>
                      <input data-testid="add-user-email" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="ravi@example.com" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600">Phone</label>
                      <input data-testid="add-user-phone" value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value.replace(/[^0-9+]/g, '') })}
                        className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="9876543210" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600">Password (required, min 6 chars)</label>
                      <input data-testid="add-user-password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm font-mono" placeholder="Type an initial password" required />
                    </div>
                  </div>

                  <button data-testid="fr-manage-create-user" onClick={createFranchiseUser} disabled={busy || !canEdit}
                    className="mt-4 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-[#7B1FA2] hover:bg-[#6A1B9A] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm"
                    title={canEdit ? '' : 'You lack Edit permission for Franchises'}>
                    {canEdit ? <UserPlus className="w-4 h-4" /> : <Lock className="w-4 h-4" />} {busy ? 'Creating…' : 'Create user'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Create Chief Accountant modal (iter-17) */}
      {showCreateCa && (
        <Modal onClose={() => setShowCreateCa(false)} title="Create Chief Accountant">
          <p className="text-xs text-neutral-500 mb-3">Creates a Head Office Chief Accountant with access to the Group Dashboard, HO expense tools, and the global approvals queue.</p>
          {/* Credentials banner removed (security policy 2026-02) */}
          <label className="block text-xs font-semibold text-neutral-600">Full name</label>
          <input data-testid="ca-form-name" value={caForm.name} onChange={(e) => setCaForm({ ...caForm, name: e.target.value })}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="Priya Menon" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Email</label>
          <input data-testid="ca-form-email" type="email" value={caForm.email} onChange={(e) => setCaForm({ ...caForm, email: e.target.value })}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="priya.ca@clean24.com" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Phone (optional)</label>
          <input data-testid="ca-form-phone" value={caForm.phone} onChange={(e) => setCaForm({ ...caForm, phone: e.target.value.replace(/[^0-9+]/g, '') })}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="9876543210" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Password (required, min 6 chars)</label>
          <input data-testid="ca-form-password" value={caForm.password} onChange={(e) => setCaForm({ ...caForm, password: e.target.value })}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm font-mono" placeholder="Type an initial password" required />
          <button data-testid="ca-form-submit" onClick={createChiefAccountant} disabled={busy || !caForm.password || caForm.password.length < 6}
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-[#4338CA] hover:bg-[#3730A3] disabled:opacity-50 text-white font-semibold text-sm">
            <ShieldCheck className="w-4 h-4" /> {busy ? 'Creating…' : 'Create Chief Accountant'}
          </button>
        </Modal>
      )}
    </AdminLayout>
  );
};

const Fld = ({ label, value, onChange, testid, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-xs font-semibold text-neutral-600">{label}</label>
    <input data-testid={testid} value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" />
  </div>
);

const Stat = ({ label, value, icon: Icon, color, bg, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-xl p-4">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: bg }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="mt-3 text-xs text-neutral-500 font-medium">{label}</p>
    <p className="font-extrabold text-xl mt-0.5">{value}</p>
  </div>
);
const Th = ({ children, className = '' }) => <th className={`px-4 py-2.5 text-xs font-bold text-neutral-500 uppercase tracking-wider ${className}`}>{children}</th>;

const NumField = ({ label, value, onChange, color = '#2E7D32', testid }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
    <input
      data-testid={testid}
      type="number" min="0" max="100"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      className="mt-1 w-full px-2 py-2 rounded-lg border border-neutral-200 text-center text-lg font-extrabold focus:outline-none focus:ring-2"
      style={{ color, borderColor: '#e5e7eb' }} />
  </div>
);
const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="font-extrabold">{title}</p>
        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

export default AdminFranchises;
